import React, { useState, useEffect, useRef, useCallback } from 'react';
import { client } from '../client';
import { motion } from 'framer-motion';
import imageUrlBuilder from '@sanity/image-url';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

const builder = imageUrlBuilder(client);

function urlFor(source) {
  return builder.image(source);
}

const CATEGORIES = [
  { label: 'Portraits', value: 'portraits' },
  { label: 'Self Portraits', value: 'self-portraits' },
  { label: 'Travel & Nature', value: 'travel-nature' },
];

export default function Portfolio() {
  const [galleries, setGalleries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(null);

  const theme = useTheme();
  const darkMode = theme?.darkMode ?? true;

  useEffect(() => {
    client
      .fetch(`*[_type == "gallery" && category != "home"] | order(orderNumber asc)`)
      .then((data) => {
        setGalleries(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      selectedIndex !== null ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const total = modalImages.length;

  const goNext = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === null || total === 0 ? 0 : prev === total - 1 ? 0 : prev + 1
    );
  }, [total]);

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === null || total === 0 ? 0 : prev === 0 ? total - 1 : prev - 1
    );
  }, [total]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') setSelectedIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goNext, goPrev]);

  const openModal = (images, index) => {
    setModalImages(images);
    setSelectedIndex(index);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) {
      deltaX < 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <main
        style={{
          background: darkMode
            ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
            : '#ffffff',
        }}
        className="min-h-screen"
      />
    );
  }

  return (
    <>
      <main
        style={{
          background: darkMode
            ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
            : '#ffffff',
          color: darkMode ? '#ffffff' : '#0f172a',
          transition: 'all 0.3s ease',
        }}
        className="min-h-screen w-full flex flex-col overflow-x-hidden"
      >
        <div className="max-w-7xl px-5 mx-auto w-full">
          {CATEGORIES.map((cat) => {
            const categoryGalleries = galleries.filter(
              (g) => g.category === cat.value
            );
            const images = categoryGalleries.flatMap((g) => g.images || []);
            if (!images.length) return null;

            return (
              <motion.section
                key={cat.value}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 1 }}
                className="py-14"
              >
                <h2 className={`h1 text-left font-bold mb-10 sm:text-7xl ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {cat.label}
                </h2>

                <div className="columns-2 lg:columns-3 gap-3">
                  {images.map((img, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.03 }}
                      className="mb-3 break-inside-avoid overflow-hidden cursor-pointer"
                      onClick={() => openModal(images, index)}
                    >
                      <img
                        src={urlFor(img).width(1000).url()}
                        alt={`${cat.label}-${index}`}
                        className="w-full h-auto object-contain"
                        loading="lazy"
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            );
          })}
        </div>

        {/* Modal */}
        {selectedIndex !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setSelectedIndex(null)}
            />

            {/* X button — mobile only */}
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 z-20 text-white text-3xl font-light sm:hidden"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Arrows — desktop only */}
            <button
              onClick={goPrev}
              className="absolute left-4 z-20 text-white text-7xl font-extralight px-6 py-4 hidden sm:block"
            >
              ‹
            </button>

            <motion.img
              key={selectedIndex}
              src={urlFor(modalImages[selectedIndex]).url()}
              alt="full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative z-10 object-contain max-w-[92vw] lg:max-w-[70vw] max-h-[88vh]"
            />

            <button
              onClick={goNext}
              className="absolute right-4 z-20 text-white text-7xl font-extralight px-6 py-4 hidden sm:block"
            >
              ›
            </button>
          </div>
        )}

        <Footer />
      </main>
    </>
  );
}